/**
 * The crew call sheet: one row per person — who, job, camera, place, status, notes — editable in
 * place by a producer in two taps (tap the Job / Camera / Place chip → tap the choice). The same
 * component is the TEAM tab's list and the Crew tab of the Setup sheet, so it never opens a second
 * modal: choices and details unfold under the row.
 *
 * Job (what they do on the shoot) is on the row; app access (what they may change in Airview Studio) is in
 * the person's details, labelled as such.
 */
import { useState, type ReactNode } from 'react';
import { CREW_JOB_LABEL, CREW_JOBS, type CrewJob, type MissionBundle, type Role } from '@apos/shared';
import { Button, Chip, cx, Field, Icon, IconButton, Select, TextArea, TextInput, toast, type ChipTone, type IconName } from '../../components/ui';
import { atLeast, ROLE_LABEL } from '../../app/labels';
import { useSetupSheet } from '../setup';
import type { Mutate } from '../setup/mutate';
import { crewActions, type CrewActions } from './crewActions';
import { ReportButton } from '../moderation/ReportBlockDialog';
import type { TeamRow, TeamStatus } from './teamModel';

export const STATUS_TONE: Record<TeamStatus, ChipTone> = {
  live: 'live',
  aging: 'aging',
  stale: 'stale',
  waiting: 'neutral',
  not_sharing: 'neutral',
  no_app: 'neutral',
  placed: 'manual',
  no_position: 'neutral',
};

const ACCESS_HINT: Record<Role, string> = {
  owner: 'Can change everything, including who has access.',
  producer: 'Can set up the shoot and assign the crew.',
  crew: 'Can share location and re-aim the cameras they run.',
  viewer: 'Can look, not change.',
};

/** "Kofi Ortiz" → "KO", "Bram (sim)" → "B". */
export function initials(name: string): string {
  return name
    .replace(/\(.*?\)/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

type Open = { key: string; what: 'job' | 'camera' | 'place' | 'details' } | null;

export interface CallSheetProps {
  bundle: MissionBundle;
  rows: readonly TeamRow[];
  /** Tap a person's name: show them on the map (TEAM). Without it, the name opens their details. */
  onShow?(r: TeamRow): void;
  mutate?: Mutate;
}

export function CallSheet({ bundle, rows, onShow, mutate }: CallSheetProps) {
  const [open, setOpen] = useState<Open>(null);
  const act = crewActions(bundle, mutate);
  const people = rows.filter((r) => r.kind === 'person');
  const toggle = (key: string, what: NonNullable<Open>['what']) => setOpen((o) => (o?.key === key && o.what === what ? null : { key, what }));
  return (
    <ol className="callsheet" aria-label="Crew call sheet" data-testid="call-sheet">
      {people.map((r) => (
        <CrewRow key={r.key} r={r} bundle={bundle} rows={rows} act={act} open={open?.key === r.key ? open.what : null} toggle={(w) => toggle(r.key, w)} close={() => setOpen(null)} onShow={onShow} />
      ))}
    </ol>
  );
}

function CrewRow({
  r,
  bundle,
  rows,
  act,
  open,
  toggle,
  close,
  onShow,
}: {
  r: TeamRow;
  bundle: MissionBundle;
  rows: readonly TeamRow[];
  act: CrewActions;
  open: NonNullable<Open>['what'] | null;
  toggle(w: NonNullable<Open>['what']): void;
  close(): void;
  onShow?(r: TeamRow): void;
}) {
  const canProduce = atLeast(bundle.myRole, 'producer');
  const canAssign = canProduce && r.editable;
  const canDetails = r.editable && (canProduce || r.you);
  const camText = r.cameras.length ? r.cameras.map((c) => c.label).join(', ') : null;
  const lens = r.cameras.length === 1 ? r.cameras[0]!.lens : null;
  const name = (
    <>
      <span className={cx('crew-row__dot', !r.userId && 'crew-row__dot--noapp')} aria-hidden style={r.color ? { background: r.color } : undefined}>
        {initials(r.name) || <Icon name="user" size={14} />}
      </span>
      <span className="crew-row__id">
        <span className="crew-row__name">
          <span className="crew-row__nametext">{r.name}</span>
          {r.you ? (
            <Chip size="sm" tone="accent">
              YOU
            </Chip>
          ) : null}
        </span>
        {r.where ? <span className="crew-row__where">{r.where}</span> : null}
      </span>
    </>
  );
  return (
    <li className={cx('crew-row', r.you && 'crew-row--you')} data-testid="crew-row" data-name={r.name}>
      <div className="crew-row__top">
        {onShow && r.position ? (
          <button type="button" className="crew-row__who" onClick={() => onShow(r)} aria-label={`${r.name}${r.you ? ' (you)' : ''}${r.where ? `, ${r.where}` : ''}, show on map`}>
            {name}
          </button>
        ) : canDetails ? (
          <button type="button" className="crew-row__who" onClick={() => toggle('details')} aria-label={`${r.name}${r.you ? ' (you)' : ''}: details`}>
            {name}
          </button>
        ) : (
          <div className="crew-row__who">{name}</div>
        )}
        <div className="crew-row__status">
          <Chip size="sm" tone={STATUS_TONE[r.status]} dot={r.status === 'live' || r.status === 'aging'}>
            {r.statusText}
          </Chip>
          {r.inFrame ? (
            <Chip size="sm" tone="live" icon="camera">
              Has it in frame
            </Chip>
          ) : null}
        </div>
        {r.userId && !r.you ? <ReportButton target={{ missionId: bundle.mission.id, contentType: 'user', userId: r.userId, name: r.name }} className="report-btn crew-row__report" /> : null}
        {canDetails ? <IconButton icon="more" label={`Details for ${r.name}`} onClick={() => toggle('details')} aria-expanded={open === 'details'} /> : null}
      </div>
      <div className="crew-row__chips">
        <Assign icon="user" label="Job" person={r.name} value={r.job ? CREW_JOB_LABEL[r.job] : null} editable={canAssign} on={open === 'job'} onClick={() => toggle('job')} testid="assign-job" />
        <Assign icon="camera" label="Camera" person={r.name} value={camText ? `${camText}${lens ? ` · ${lens}` : ''}` : null} editable={canAssign} on={open === 'camera'} onClick={() => toggle('camera')} testid="assign-camera" />
        <Assign icon="pin" label="Place" person={r.name} value={r.placeName} editable={canAssign} on={open === 'place'} onClick={() => toggle('place')} testid="assign-place" />
      </div>
      {r.notes ? <p className="crew-row__notes">{r.notes}</p> : null}

      {open === 'job' ? <JobPick r={r} act={act} close={close} /> : null}
      {open === 'camera' ? <CameraPick r={r} bundle={bundle} rows={rows} act={act} close={close} /> : null}
      {open === 'place' ? <PlacePick r={r} bundle={bundle} act={act} close={close} /> : null}
      {open === 'details' ? <Details r={r} bundle={bundle} act={act} close={close} /> : null}
    </li>
  );
}

/** One assignment on the row: a chip that opens its choices (producer), or plain text. */
function Assign({ icon, label, person, value, editable, on, onClick, testid }: { icon: IconName; label: string; person: string; value: string | null; editable: boolean; on: boolean; onClick(): void; testid: string }) {
  if (!editable) {
    return value ? (
      <span className="assign assign--ro" data-testid={testid}>
        <Icon name={icon} size={13} />
        <span className="assign__text">{value}</span>
      </span>
    ) : null;
  }
  return (
    <button type="button" className={cx('assign', !value && 'assign--empty', on && 'is-on')} onClick={onClick} aria-expanded={on} aria-label={`${label} for ${person}: ${value ?? 'not set'}. Change`} data-testid={testid}>
      <Icon name={value ? icon : 'plus'} size={13} />
      <span className="assign__text">{value ?? label}</span>
    </button>
  );
}

function Pick({ label, children, foot }: { label: string; children: ReactNode; foot?: ReactNode }) {
  return (
    <div className="crew-pick" role="group" aria-label={label}>
      <div className="crew-pick__opts">
        {children}
        {foot}
      </div>
    </div>
  );
}

function Opt({ on, children, onClick, sub, disabled }: { on?: boolean; children: ReactNode; onClick(): void; sub?: string | null; disabled?: boolean }) {
  return (
    <button type="button" className={cx('crew-opt', on && 'is-on')} aria-pressed={!!on} onClick={onClick} aria-disabled={disabled || undefined}>
      {on ? <Icon name="check" size={13} /> : null}
      <span>{children}</span>
      {sub ? <span className="crew-opt__sub">{sub}</span> : null}
    </button>
  );
}

function JobPick({ r, act, close }: { r: TeamRow; act: CrewActions; close(): void }) {
  const pick = async (job: CrewJob | null) => {
    close();
    if (job !== r.job) await act.setJob(r.personId!, job);
  };
  return (
    <Pick label={`Job for ${r.name}`}>
      {CREW_JOBS.map((j) => (
        <Opt key={j.value} on={r.job === j.value} onClick={() => void pick(j.value)}>
          {j.label}
        </Opt>
      ))}
      {r.job ? <Opt onClick={() => void pick(null)}>No job</Opt> : null}
    </Pick>
  );
}

function CameraPick({ r, bundle, rows, act, close }: { r: TeamRow; bundle: MissionBundle; rows: readonly TeamRow[]; act: CrewActions; close(): void }) {
  const ownerName = (camId: string) => rows.find((x) => x.kind === 'person' && x.cameras.some((c) => c.id === camId))?.name ?? null;
  const mine = new Set(r.cameras.map((c) => c.id));
  const pick = async (camId: string) => {
    const cam = bundle.cameras.find((c) => c.id === camId)!;
    if (mine.has(camId)) {
      if (cam.mount === 'crew') {
        toast({ title: `${cam.label} needs someone`, body: `Give it to someone else to take it off ${r.name}.`, timeoutMs: 3000 });
        return;
      }
      close();
      await act.giveCamera(camId, null);
      return;
    }
    close();
    await act.giveCamera(camId, r.personId);
  };
  return (
    <Pick
      label={`Camera for ${r.name}`}
      foot={
        <button type="button" className="crew-opt crew-opt--new" onClick={() => useSetupSheet.getState().open({ kind: 'add', tab: 'camera', cameraType: 'manned' })}>
          <Icon name="plus" size={13} />
          <span>New camera</span>
        </button>
      }
    >
      {bundle.cameras.length ? (
        bundle.cameras.map((c) => {
          const who = ownerName(c.id);
          return (
            <Opt key={c.id} on={mine.has(c.id)} onClick={() => void pick(c.id)} sub={mine.has(c.id) ? null : who ?? (c.mount === 'crew' ? null : 'nobody')}>
              {c.label}
            </Opt>
          );
        })
      ) : (
        <span className="muted small">No cameras yet.</span>
      )}
    </Pick>
  );
}

function PlacePick({ r, bundle, act, close }: { r: TeamRow; bundle: MissionBundle; act: CrewActions; close(): void }) {
  const pick = async (id: string | null) => {
    close();
    if (id !== r.placeId) await act.setPlace(r.personId!, id);
  };
  return (
    <Pick
      label={`Place for ${r.name}`}
      foot={
        <button type="button" className="crew-opt crew-opt--new" onClick={() => useSetupSheet.getState().open({ kind: 'add', tab: 'place', placeType: 'team' })}>
          <Icon name="plus" size={13} />
          <span>New place</span>
        </button>
      }
    >
      {bundle.locations.length ? (
        bundle.locations.map((l) => (
          <Opt key={l.id} on={r.placeId === l.id} onClick={() => void pick(l.id)}>
            {l.name}
          </Opt>
        ))
      ) : (
        <span className="muted small">No places yet.</span>
      )}
      {r.placeId ? <Opt onClick={() => void pick(null)}>No place</Opt> : null}
    </Pick>
  );
}

/** Name / phone / notes, app access (members), link to a member who joined, remove. */
function Details({ r, bundle, act, close }: { r: TeamRow; bundle: MissionBundle; act: CrewActions; close(): void }) {
  const canProduce = atLeast(bundle.myRole, 'producer');
  const isOwner = bundle.myRole === 'owner';
  const [name, setName] = useState(r.name);
  const [phone, setPhone] = useState(r.phone ?? '');
  const [notes, setNotes] = useState(r.notes);
  const [linkTo, setLinkTo] = useState('');
  const [busy, setBusy] = useState(false);
  const noApp = !r.userId;
  const save = async () => {
    const p: { name?: string; phone?: string | null; notes?: string } = {};
    if (noApp && canProduce && name.trim() && name.trim() !== r.name) p.name = name.trim();
    if ((phone.trim() || null) !== r.phone) p.phone = phone.trim() || null;
    if (notes !== r.notes) p.notes = notes;
    if (!Object.keys(p).length) return close();
    setBusy(true);
    const ok = await act.save(r.personId!, p);
    setBusy(false);
    if (ok) close();
  };
  const members = bundle.members.filter((m) => !m.isSimulated);
  return (
    <div className="crew-details stack-12" data-testid="crew-details">
      {noApp && canProduce ? <Field label="Name">{(id) => <TextInput id={id} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />}</Field> : null}
      <div className="crew-details__phone">
        <Field label="Phone" optional>
          {(id) => <TextInput id={id} type="tel" inputMode="tel" mono value={phone} maxLength={32} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 0100" />}
        </Field>
        {r.phone ? (
          <a className="btn btn--secondary btn--md crew-details__call" href={`tel:${r.phone.replace(/[^\d+]/g, '')}`}>
            <Icon name="phone" size={16} />
            <span className="btn__label">Call</span>
          </a>
        ) : null}
      </div>
      <Field label="Notes" optional hint="Call time, what to bring, anything for the day.">
        {(id) => <TextArea id={id} rows={2} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Call time 6:30 · bring the 600 mm" />}
      </Field>
      <div className="setup-foot">
        <span style={{ flex: 1 }} />
        <Button variant="ghost" size="sm" onClick={close}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" loading={busy} onClick={() => void save()}>
          Save
        </Button>
      </div>

      {r.access ? (
        <div className="crew-access" aria-label="App access">
          <span className="field__label">App access (what they can change in Airview Studio)</span>
          {isOwner && !r.you ? (
            <Select aria-label={`App access for ${r.name}`} value={r.access} onChange={(e) => void act.setAccess(r.userId!, e.target.value as Role)}>
              {(['owner', 'producer', 'crew', 'viewer'] as Role[]).map((x) => (
                <option key={x} value={x}>
                  {ROLE_LABEL[x]}
                </option>
              ))}
            </Select>
          ) : (
            <b>{ROLE_LABEL[r.access]}</b>
          )}
          <span className="muted small">{ACCESS_HINT[r.access]}</span>
        </div>
      ) : (
        <p className="muted small" style={{ margin: 0 }}>
          No app — they show at their camera or their place.
        </p>
      )}

      {noApp && canProduce && members.length ? (
        <div className="crew-link">
          <Select aria-label={`${r.name} has the app now`} value={linkTo} onChange={(e) => setLinkTo(e.target.value)}>
            <option value="">They have the app now — this is…</option>
            {members.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.displayName || m.callsign}
              </option>
            ))}
          </Select>
          <Button size="sm" icon="link" disabled={!linkTo} onClick={async () => (await act.link(r.personId!, linkTo)) && close()}>
            Link
          </Button>
        </div>
      ) : null}
      {noApp && canProduce ? (
        <Button variant="danger" size="sm" icon="trash" onClick={async () => (await act.remove(r.personId!)) && close()}>
          Remove {r.name}
        </Button>
      ) : null}
    </div>
  );
}

/** Add someone without the app: name, job, phone. Cameras and places are then two taps on their row. */
export function AddPersonForm({ bundle, mutate, onDone }: { bundle: MissionBundle; mutate?: Mutate; onDone(): void }) {
  const [name, setName] = useState('');
  const [job, setJob] = useState<CrewJob | ''>('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    const r = await crewActions(bundle, mutate).add({ name: name.trim(), job: job || null, phone: phone.trim() || null });
    setBusy(false);
    if (r) {
      toast({ title: `${r.name} added`, body: 'Tap Camera or Place on their row to assign.', timeoutMs: 2500 });
      onDone();
    }
  };
  return (
    <form
      className="crew-add stack-12"
      data-testid="add-person"
      onSubmit={(e) => {
        e.preventDefault();
        void add();
      }}
    >
      <div className="eyebrow">Someone without the app</div>
      <Field label="Name">{(id) => <TextInput id={id} autoFocus value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Kofi" />}</Field>
      <div className="form-row">
        <Field label="Job" optional>
          {(id) => (
            <Select id={id} value={job} onChange={(e) => setJob(e.target.value as CrewJob | '')}>
              <option value="">—</option>
              {CREW_JOBS.map((j) => (
                <option key={j.value} value={j.value}>
                  {j.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Phone" optional>
          {(id) => <TextInput id={id} type="tel" inputMode="tel" mono value={phone} maxLength={32} onChange={(e) => setPhone(e.target.value)} />}
        </Field>
      </div>
      <div className="setup-foot">
        <span className="muted small" style={{ flex: 1 }}>
          Has the app? Invite them instead.
        </span>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" type="submit" loading={busy} disabled={!name.trim()}>
          Add
        </Button>
      </div>
    </form>
  );
}
